import moment from 'moment';

const ISO_DAY = 'YYYY-MM-DD';

type DateLike = Date | string | null | undefined;

const toDay = (date: DateLike): string | null => {
  if (!date) return null;
  const parsed = typeof date === 'string' ? moment(date, ISO_DAY, true) : moment(date);
  return parsed.isValid() ? parsed.format(ISO_DAY) : null;
};

export type BackdatedPayerNoteInput = {
  /** `docs-due-anchored-mode` for the facility; null while Split is not ready. */
  isAnchoredMode: boolean | null;
  action: 'ADD' | 'EDIT';
  startDate: DateLike;
  /** The payer's saved start date (EDIT only). */
  initialStartDate?: DateLike;
  payerPlanId?: string | null;
  /** The payer's saved plan (EDIT only). */
  initialPayerPlanId?: string | null;
  /** Facility-local today, yyyy-MM-dd. */
  todayYmd: string;
};

/**
 * THIN-8288: whether the payer form shows the "rules apply from today" note.
 *
 * Under ANCHORED docs-due (THIN-8237) a payer entered or changed today drives Docs Due from today,
 * whatever its start date. Under FULL the whole open cycle is recalculated from the start date, so the
 * note would be false: it shows only when the toggle is on, and not while Split is still loading.
 *
 * On EDIT the saved start date is usually already past, so the note waits for a docs-due relevant
 * change (start date or plan) instead of showing as soon as the form opens.
 */
export const shouldShowBackdatedPayerNote = ({
  isAnchoredMode,
  action,
  startDate,
  initialStartDate,
  payerPlanId,
  initialPayerPlanId,
  todayYmd,
}: BackdatedPayerNoteInput): boolean => {
  if (isAnchoredMode !== true) return false;
  const start = toDay(startDate);
  if (!start || start >= todayYmd) return false;
  if (action === 'ADD') return true;
  const startChanged = start !== toDay(initialStartDate);
  const planChanged = Boolean(payerPlanId) && payerPlanId !== initialPayerPlanId;
  return startChanged || planChanged;
};

/** THIN-8288 copy (draft, pending Darren). Dated with the facility's today, e.g. "Oct 29". */
export const backdatedPayerNoteText = (todayYmd: string): string =>
  `Documentation due dates follow this payer's rules starting today (${moment(
    todayYmd,
    ISO_DAY
  ).format('MMM D')}), not from the start date. Due dates before today won't change.`;

/** THIN-8288: the overlap popup's closing sentence, which reads as retroactive under ANCHORED mode. */
export const endCurrentPayerDocsDueSentence = (isAnchoredMode: boolean | null): string =>
  isAnchoredMode === true
    ? 'Docs-due alerts will follow the new payer from today.'
    : 'Docs-due alerts will update automatically.';
